import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from "./ui/button";
import Spinner from "./ui/spinner";
import SmartActionButton from "./ui/SmartActionButton";
import WorkflowGuide from "./ui/WorkflowGuide";

const NodeForm = ({
    selectedNode,
    nodeName,
    setNodeName,
    nodeType,
    setNodeType,
    technology,
    setTechnology,
    status,
    setStatus,
    featureTeam,  // 👈 NOUVEAU : Feature team prop
    setFeatureTeam,  // 👈 NOUVEAU : Feature team setter
    nodeDescription,
    setNodeDescription,
    metadata,
    setMetadata,
    endpointSchema,
    setEndpointSchema,
    loading,
    saveNode,
    updateNodeSimple,
    resetForm,
    setPreviewNode,
    setShowPreview,
    setPreviewError,
    addPendingNode,  // 👈 NOUVEAU : Prop pour ajouter aux pending nodes
}) => {
    const { t } = useTranslation();
    // Effect to reset JSON fields when selectedNode changes
    useEffect(() => {
        // If no node is selected, clear JSON fields
        if (!selectedNode) {
            setMetadata("");
            setEndpointSchema("");
        }
        // If a node is selected, format JSON fields correctly
        else {
            // Format metadata if present
            if (selectedNode.metadata) {
                try {
                    const formatted = JSON.stringify(selectedNode.metadata, null, 2);
                    setMetadata(formatted);
                } catch (e) {
                    setMetadata("{}");
                    console.error("JSON metadata formatting error:", e);
                }
            } else {
                setMetadata("");
            }

            // Format endpointSchema if present
            if (selectedNode.endpointSchema) {
                try {
                    const formatted = JSON.stringify(selectedNode.endpointSchema, null, 2);
                    setEndpointSchema(formatted);
                } catch (e) {
                    setEndpointSchema("{}");
                    console.error("JSON endpointSchema formatting error:", e);
                }
            } else {
                setEndpointSchema("");
            }
        }
    }, [selectedNode, setMetadata, setEndpointSchema]);

    // Debounced description state for smoother UX
    const [localDescription, setLocalDescription] = useState(nodeDescription || "");
    const debounceTimeout = useRef();
    
    // State for workflow guide
    const [showWorkflowGuide, setShowWorkflowGuide] = useState(false);

    // Sync local state when nodeDescription prop changes (e.g., node switch)
    useEffect(() => {
        setLocalDescription(nodeDescription || "");
    }, [nodeDescription]);

    // Debounce update to parent state
    useEffect(() => {
        if (localDescription === nodeDescription) return;
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            setNodeDescription(localDescription);
        }, 300);
        return () => clearTimeout(debounceTimeout.current);
    }, [localDescription]);

    return (
        <form
            className="modern-node-form"
            onSubmit={(e) => e.preventDefault()}
        >
            <div className="form-grid">
                {/* Informations de base */}
                <div className="form-section basic-info">
                    <h5 className="section-subtitle">
                        <span className="subtitle-icon">📋</span>
                        {t('nodeForm.basicInfo')}
                    </h5>

                    <div className="input-group">
                        <label className="input-label required-field-label">
                            <span className="label-icon">🏷️</span>
                            <span className="label-text">{t('nodeForm.fields.nodeName')}</span>
                            <span className="required-asterisk">*</span>
                        </label>
                        <input
                            type="text"
                            className="create-node-name-input"
                            value={nodeName}
                            onChange={(e) => setNodeName(e.target.value)}
                            placeholder={t('nodeForm.placeholders.nodeName')}
                            required
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label required-field-label">
                            <span className="label-icon">🔧</span>
                            <span className="label-text">{t('nodeForm.fields.nodeType')}</span>
                            <span className="required-asterisk">*</span>
                        </label>
                        <select
                            className="create-node-type-select"
                            value={nodeType}
                            onChange={(e) => setNodeType(e.target.value)}
                            required
                        >
                            <option value="">{t('nodeForm.selectType')}</option>
                            <option value="Feature">🎯 Feature</option>
                            <option value="Module">📦 Module</option>
                            <option value="Component">{t('nodeForm.componentOption')}</option>
                            <option value="User">👤 User</option>
                            <option value="API">🌐 API</option>
                            <option value="Database">🗄️ Database</option>
                            <option value="Service">⚙️ Service</option>
                        </select>
                    </div>

                    <div className="input-group">
                        <label className="input-label">
                            <span className="label-icon">⚙️</span>
                            <span className="label-text">{t('nodeForm.fields.technology')}</span>
                        </label>
                        <input
                            type="text"
                            className="modern-input"
                            value={technology}
                            onChange={(e) => setTechnology(e.target.value)}
                            placeholder={t('nodeForm.placeholders.technology')}
                        />
                    </div>

                    <div className="input-group">
                        <label className="input-label">
                            <span className="label-icon">👥</span>
                            <span className="label-text">{t('nodeForm.fields.featureTeam')}</span>
                        </label>
                        <input
                            type="text"
                            className="modern-input"
                            value={featureTeam || ''}
                            onChange={(e) => setFeatureTeam(e.target.value)}
                            placeholder={t('nodeForm.placeholders.featureTeam')}
                        />
                        <div className="field-hint">
                            {t('nodeForm.hints.featureTeam')}
                        </div>
                    </div>

                    <div className="input-group">
                        <label className="input-label">
                            <span className="label-icon">📊</span>
                            <span className="label-text">{t('nodeForm.fields.status')}</span>
                        </label>
                        <select
                            className="modern-select"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                        >
                            <option value="">{t('nodeForm.selectStatus')}</option>
                            <option value="En développement">
                                🚧 {t('nodeForm.statuses.inDevelopment')}
                            </option>
                            <option value="En test">🧪 {t('nodeForm.statuses.testing')}</option>
                            <option value="Terminé">✅ {t('nodeForm.statuses.completed')}</option>
                            <option value="En attente">⏸️ {t('nodeForm.statuses.waiting')}</option>
                            <option value="Archivé">📦 {t('nodeForm.statuses.archived')}</option>
                            <option value="Prod">🚀 {t('nodeForm.statuses.production')}</option>
                        </select>
                    </div>
                </div>

                {/* Description */}
                <div className="form-section description-info">
                    <h5 className="section-subtitle">
                        <span className="subtitle-icon">📝</span>
                        {t('nodeForm.fields.description')}
                    </h5>

                    <div className="input-group">
                        <label className="input-label required-field-label">
                            <span className="label-icon">📄</span>
                            <span className="label-text">
                                {t('nodeForm.fields.detailedDescription')}
                            </span>
                            <span className="required-asterisk">*</span>
                        </label>
                        <textarea
                            className="create-node-description-textarea"
                            value={localDescription}
                            onChange={(e) => setLocalDescription(e.target.value)}
                            placeholder={t('nodeForm.placeholders.description')}
                            rows="14"
                            maxLength={1000}
                            required
                        />
                        <div className="char-counter">
                            {t('nodeForm.characterCount', { count: (localDescription || "").length })}/1000
                        </div>
                    </div>
                </div>
            </div>

            {/* Sections avancées */}
            <div className="advanced-sections">
                {/* Métadonnées */}
                <div className="form-section metadata-section">
                    <h5 className="section-subtitle">
                        <span className="subtitle-icon">🔧</span>
                        Métadonnées (JSON)
                        <span className="optional-label">Optionnel</span>
                    </h5>

                    <div className="input-group">
                        <div className="json-input-container">
                            <div className="json-input-header">
                                <span className="json-icon">{ }</span>
                                <span className="json-label">Configuration JSON</span>
                                <button
                                    type="button"
                                    className="format-json-btn"
                                    onClick={() => {
                                        try {
                                            const formatted = JSON.stringify(
                                                JSON.parse(metadata || "{}"),
                                                null,
                                                2
                                            );
                                            setMetadata(formatted);
                                        } catch (e) {
                                            toast.error(t('nodeForm.invalidJson'));
                                        }
                                    }}
                                >
                                    {t('nodeForm.formatJson')}
                                </button>
                            </div>
                            <textarea
                                className="json-textarea"
                                value={metadata}
                                onChange={(e) => setMetadata(e.target.value)}
                                placeholder={t('nodeForm.metadataPlaceholder')}
                                rows="6"
                            />
                        </div>
                    </div>
                </div>

                {/* Schéma d'endpoints */}
                <div className="form-section endpoint-section">
                    <h5 className="section-subtitle">
                        <span className="subtitle-icon">🌐</span>
                        Schéma des Endpoints (JSON)
                        <span className="optional-label">Optionnel</span>
                    </h5>

                    <div className="input-group">
                        <div className="json-input-container">
                            <div className="json-input-header">
                                <span className="json-icon">🔗</span>
                                <span className="json-label">
                                    Configuration des endpoints
                                </span>
                                <button
                                    type="button"
                                    className="format-json-btn"
                                    onClick={() => {
                                        try {
                                            const formatted = JSON.stringify(
                                                JSON.parse(endpointSchema || "{}"),
                                                null,
                                                2
                                            );
                                            setEndpointSchema(formatted);
                                        } catch (e) {
                                            toast.error(t('nodeForm.invalidJson'));
                                        }
                                    }}
                                >
                                    {t('nodeForm.formatJson')}
                                </button>
                            </div>
                            <textarea
                                className="json-textarea"
                                value={endpointSchema}
                                onChange={(e) => setEndpointSchema(e.target.value)}
                                placeholder={t('nodeForm.placeholders.apiEndpoints')}
                                rows="6"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Actions du formulaire */}
            <div className="form-actions">
                {/* Nouveau bouton intelligent qui remplace les actions conditionnelles */}
                <SmartActionButton
                    selectedNode={selectedNode}
                    nodeName={nodeName}
                    nodeType={nodeType}
                    nodeDescription={localDescription}
                    technology={technology}
                    saveNode={saveNode}
                    updateNodeSimple={updateNodeSimple}
                    loading={loading}
                    resetForm={resetForm}
                />

                {/* Action de modélisation pour la création uniquement */}
                {!selectedNode && (
                    <button
                        type="button"
                        className="main-option-btn modeling-queue"
                        onClick={async () => {
                            const node = {
                                name: nodeName,
                                description: nodeDescription,
                                type: nodeType,
                                technology,
                                featureTeam,  // 👈 NOUVEAU : Feature team
                                status: status || "En modélisation",
                                metadata: metadata ? JSON.parse(metadata) : {},
                                endpointSchema: endpointSchema
                                    ? JSON.parse(endpointSchema)
                                    : {},
                                author: "PO/QA",
                                suggestedLinks: [],
                                isInModelingQueue: true,
                                isActualNode: true,  // 👈 NOUVEAU : Flag explicite pour identifier les vrais nœuds
                            };
                            if (!node.name || !node.type) {
                                setPreviewError(
                                    "Le nom et le type sont obligatoires."
                                );
                                return;
                            }
                            // Ajouter à la file de modélisation plutôt que sauvegarder directement
                            addPendingNode(node);
                            
                            // Alerte de confirmation
                            toast.success(`✅ Nœud "${node.name}" ajouté à la file de modélisation !`);
                        }}
                        disabled={loading || !(nodeName || "").trim() || !nodeType}
                        title={t('nodeForm.addToModeling')}
                        style={{ marginTop: '12px' }}
                    >
                        <div className="option-icon">🎯</div>
                        <div className="option-content">
                            <div className="option-title">{t('nodeForm.addToModelingTitle')}</div>
                            <div className="option-description">{t('nodeForm.addToModelingDescription')}</div>
                        </div>
                        <div className="option-arrow">→</div>
                    </button>
                )}
            </div>

            {/* Guide de workflow */}
            <WorkflowGuide
                show={showWorkflowGuide}
                onClose={() => setShowWorkflowGuide(false)}
                currentContext={selectedNode ? 'modification' : 'creation'}
            />
        </form>
    );
};

export default NodeForm;